import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle2, XCircle, Users, Mail, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InviteAccept() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const autoAccepted = useRef(false);

  // Fetch invite preview (public endpoint)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/teams/invites/${inviteId}/preview`);
        if (!cancelled) {
          setInvite(res.data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Invite not found or expired');
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [inviteId]);

  // Auto-accept for logged-in user whose email matches the invite
  useEffect(() => {
    if (authLoading || loading || autoAccepted.current) return;
    if (!user || !invite || invite.status !== 'pending') return;
    if (user.email?.toLowerCase() !== invite.email?.toLowerCase()) return;

    autoAccepted.current = true;
    (async () => {
      setAccepting(true);
      try {
        await axios.post(`${API}/teams/invites/${inviteId}/accept`, {});
        toast.success(`Welcome to ${invite.team_name}!`);
        navigate('/teams', { replace: true });
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to accept invite');
      } finally {
        setAccepting(false);
      }
    })();
  }, [authLoading, loading, user, invite, inviteId, navigate]);

  const handleAcceptClick = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/teams/invites/${inviteId}/accept`, {});
      toast.success(`Welcome to ${invite.team_name}!`);
      navigate('/teams', { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        // Should not happen - we guard earlier - but just in case
        login();
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineClick = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/teams/invites/${inviteId}/decline`, {});
      toast.success('Invite declined');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline invite');
    } finally {
      setAccepting(false);
    }
  };

  // --- Render states ---

  if (loading || authLoading) {
    return (
      <PageShell>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-[#00E599] animate-spin" />
          <p className="text-slate-400">Loading invitation…</p>
        </div>
      </PageShell>
    );
  }

  if (error || !invite) {
    return (
      <PageShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <XCircle className="w-12 h-12 text-red-400" />
          <h1 className="text-2xl font-bold">Invite unavailable</h1>
          <p className="text-slate-400 max-w-sm">{error || 'This invitation link is invalid or has already been used.'}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#00E599] text-black hover:bg-[#00CC88] mt-4"
            data-testid="invite-back-home-btn"
          >
            Back to home
          </Button>
        </div>
      </PageShell>
    );
  }

  if (invite.status !== 'pending') {
    return (
      <PageShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <CheckCircle2 className="w-12 h-12 text-slate-500" />
          <h1 className="text-2xl font-bold">Invite already {invite.status}</h1>
          <p className="text-slate-400 max-w-sm">This invitation is no longer active.</p>
          <Button
            onClick={() => navigate(user ? '/teams' : '/')}
            className="bg-[#00E599] text-black hover:bg-[#00CC88] mt-4"
            data-testid="invite-go-btn"
          >
            {user ? 'Go to Teams' : 'Back to home'}
          </Button>
        </div>
      </PageShell>
    );
  }

  // Logged in but different email than invite target
  const emailMismatch =
    user && invite.email && user.email?.toLowerCase() !== invite.email.toLowerCase();

  return (
    <PageShell>
      <Card className="bg-[#111827] border-white/10 rounded-xl w-full max-w-lg" data-testid="invite-card">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#00E599]/10 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-[#00E599]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Team Invitation</p>
              <h1 className="text-xl font-bold text-white" data-testid="invite-team-name">
                {invite.team_name}
              </h1>
            </div>
          </div>

          <p className="text-slate-300 mb-6 leading-relaxed">
            <span className="font-semibold text-white">{invite.inviter_name}</span>
            {invite.inviter_email && (
              <span className="text-slate-500"> ({invite.inviter_email})</span>
            )}{' '}
            invited <span className="font-mono text-[#00E599]">{invite.email}</span> to join their AutoQA.ai team.
          </p>

          {invite.team_description && (
            <div className="mb-6 p-4 bg-black/30 rounded-lg border border-white/5">
              <p className="text-xs uppercase text-slate-500 mb-1">About this team</p>
              <p className="text-sm text-slate-300">{invite.team_description}</p>
            </div>
          )}

          {/* Action area - depends on auth state */}
          {!user && (
            <div className="space-y-3">
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg flex gap-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">
                  Sign in with <span className="font-mono text-blue-300">{invite.email}</span> to accept this invitation.
                </p>
              </div>
              <Button
                className="w-full bg-[#00E599] text-black hover:bg-[#00CC88] font-semibold"
                onClick={login}
                data-testid="invite-signin-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in to accept
              </Button>
            </div>
          )}

          {user && emailMismatch && (
            <div className="space-y-3">
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-amber-200">
                  You're signed in as <span className="font-mono">{user.email}</span>, but this invite is for{' '}
                  <span className="font-mono">{invite.email}</span>. Sign out and sign in with the correct email to accept.
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full border-white/10"
                onClick={() => navigate('/')}
                data-testid="invite-go-home-btn"
              >
                Back to home
              </Button>
            </div>
          )}

          {user && !emailMismatch && (
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] font-semibold"
                onClick={handleAcceptClick}
                disabled={accepting}
                data-testid="invite-accept-btn"
              >
                {accepting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Accept &amp; Join
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="border-white/10 text-slate-300 hover:text-white"
                onClick={handleDeclineClick}
                disabled={accepting}
                data-testid="invite-decline-btn"
              >
                Decline
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#030712] text-white flex items-center justify-center p-6">
      <div className="fixed inset-0 hero-glow" />
      <div className="relative z-10 w-full flex flex-col items-center">{children}</div>
    </div>
  );
}
